/*
 * Copyright (c) 2023 - 2026 Bluemate B.V.
 */

import {ResponseData, useEvent} from '@dvrd/dvr-controls';
import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import FailedPayment from '../../models/failedPaymentModel';
import {hasAdminLogin} from '../../util/adminUtil.ts';
import {PaymentFailureStatus} from '../../util/interfaces';
import {isPaywallExemptPath} from '../../util/constants';
import {createErrorMessage, userHasSubscription} from '../../util/utils';
import {AppContext} from '../misc/appContext';
import TrialEnd from './trialEnd';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

export default function TrialEndController() {
    const context = useContext(AppContext);
    const {t} = useTranslation();
    const [active, setActive] = useState(false);
    const [failedPayment, setFailedPayment] = useState<FailedPayment | null>(null);
    const [loading, setLoading] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    // The paywall blocks every page EXCEPT the paywall-exempt routes (settings, admin, and the iDEAL /
    // Bol-connection flows — see PAYWALL_EXEMPT_ROUTES), so a user whose subscription has ended can
    // still start a subscription (incl. paying with iDEAL), connect Bol, and export their invoices.
    // Because the dialog only dims the content (backdropVariant='content'), the header, logout and
    // navigation menu stay interactive — settings is always reachable, so no page can trap the user.
    const canBlock = !isPaywallExemptPath(location.pathname) && !hasAdminLogin();
    // useCallback so the global mount doesn't re-subscribe the event on every navigation/render —
    // only when canBlock actually flips.
    const onActivate = useCallback(() => {
        if (!canBlock) return;
        setActive(true);
        setButtonDisabled(false);
    }, [canBlock]);
    useEvent('onTrialEnd', onActivate, canBlock);

    function onProceed() {
        if (failedPayment) {
            if (failedPayment.paymentUrl) window.open(failedPayment.paymentUrl, '_blank');
            else onGenerateNew();
        } else {
            navigate('/instellingen/abonnement');
            setActive(false);
        }
    }

    function onGenerateNew() {
        if (!failedPayment) return;
        setLoading(true);
        failedPayment.generateInvoice({
            callback: (failedPayment: FailedPayment, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    setButtonDisabled(true);
                    setFailedPayment(failedPayment);
                    window.open(failedPayment.paymentUrl, '_blank');
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'trialEnd.generateInvoiceFailed',
                                    'Het genereren van een nieuwe factuur is niet gelukt.'
                                )
                        ),
                        t('trialEnd.invoiceFailed', 'Factuur mislukt')
                    );
            }
        });
    }

    useEffect(() => {
        setFailedPayment(context.userContext.user?.subscription?.failedPayment ?? null);
    }, [context.userContext.user?.subscription?.failedPayment]);

    useEffect(() => {
        if (!canBlock) {
            setActive(false);
            return;
        }
        const failedPayment = context.userContext.user?.subscription?.failedPayment;
        const hasOpenPayment = !!failedPayment && failedPayment.status !== PaymentFailureStatus.CLOSED;
        const active = !userHasSubscription(context.userContext.user) || hasOpenPayment;
        setActive(active);
    }, [context.userContext.user, context.userContext.user?.subscription?.failedPayment, canBlock]);

    return (
        <TrialEnd
            onProceed={onProceed}
            active={active}
            failedPayment={failedPayment}
            loading={loading}
            buttonDisabled={buttonDisabled}
        />
    );
}
